import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';

export default function Navbar() {
  const { t, lang, setLang } = useLanguage();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/rsvp', label: t('nav.rsvp') },
    { to: '/guestbook', label: t('nav.guestbook') },
    { to: '/hotels', label: t('nav.hotels') },
    { to: '/erlangen', label: t('nav.erlangen') },
    { to: '/admin', label: t('nav.admin') },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: 'rgba(13, 27, 62, 0.92)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(201, 168, 76, 0.3)',
        boxShadow: '0 2px 16px rgba(0,0,0,0.3)',
      }}
    >
      <div
        style={{
          maxWidth: '1100px',
          margin: '0 auto',
          padding: '0 1.5rem',
          height: '70px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Brand */}
        <Link
          to="/"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.65rem',
            textDecoration: 'none',
            flexShrink: 0,
          }}
          onClick={() => setMenuOpen(false)}
        >
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '50%',
              border: '2px solid var(--gold)',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <img
              src="/papa_adult.png"
              alt="Michel"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
            />
          </div>
          <span
            style={{
              fontFamily: "'Cinzel', serif",
              fontSize: '1.1rem',
              color: 'var(--gold)',
              fontWeight: 600,
              letterSpacing: '0.15em',
            }}
          >
            Michel &middot; 50 ans
          </span>
        </Link>

        {/* Desktop nav */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
          }}
          className="desktop-nav"
        >
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              style={{
                color: isActive(link.to) ? 'var(--gold)' : 'rgba(245,233,198,0.85)',
                textDecoration: 'none',
                padding: '0.5rem 0.9rem',
                fontFamily: "'Cinzel', serif",
                fontSize: '0.75rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                borderBottom: isActive(link.to) ? '2px solid var(--gold)' : '2px solid transparent',
                transition: 'color 0.2s',
              }}
            >
              {link.label}
            </Link>
          ))}

          {/* Language switcher */}
          <div style={{ display: 'flex', gap: '0.3rem', marginLeft: '1rem' }}>
            {['fr', 'de'].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                style={{
                  background: lang === l ? 'var(--gold)' : 'transparent',
                  color: lang === l ? 'var(--navy)' : 'rgba(255,255,255,0.7)',
                  border: lang === l ? 'none' : '1px solid rgba(255,255,255,0.4)',
                  padding: '0.3rem 0.65rem',
                  borderRadius: '3px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  transition: 'all 0.2s',
                  letterSpacing: '0.05em',
                }}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Hamburger button (mobile) */}
        <button
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="Toggle navigation menu"
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.5rem',
            color: 'var(--gold)',
          }}
          className="hamburger-btn"
        >
          <div style={{ width: '22px', height: '2px', background: 'currentColor', marginBottom: '5px', transition: 'transform 0.2s', transform: menuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }} />
          <div style={{ width: '22px', height: '2px', background: 'currentColor', marginBottom: '5px', opacity: menuOpen ? 0 : 1, transition: 'opacity 0.2s' }} />
          <div style={{ width: '22px', height: '2px', background: 'currentColor', transition: 'transform 0.2s', transform: menuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }} />
        </button>
      </div>

      {/* Mobile menu overlay */}
      {menuOpen && (
        <div
          style={{
            backgroundColor: 'var(--navy)',
            borderTop: '1px solid rgba(201,168,76,0.3)',
            padding: '1rem 1.5rem',
          }}
          className="mobile-nav"
        >
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMenuOpen(false)}
              style={{
                display: 'block',
                color: isActive(link.to) ? 'var(--gold)' : 'rgba(245,233,198,0.85)',
                textDecoration: 'none',
                padding: '0.75rem 0',
                fontFamily: "'Cinzel', serif",
                fontSize: '0.75rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                borderBottom: '1px solid rgba(201,168,76,0.15)',
              }}
            >
              {link.label}
            </Link>
          ))}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            {['fr', 'de'].map((l) => (
              <button
                key={l}
                onClick={() => { setLang(l); setMenuOpen(false); }}
                style={{
                  background: lang === l ? 'var(--gold)' : 'transparent',
                  color: lang === l ? 'var(--navy)' : 'rgba(255,255,255,0.7)',
                  border: lang === l ? 'none' : '1px solid rgba(255,255,255,0.4)',
                  padding: '0.4rem 0.8rem',
                  borderRadius: '3px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                }}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .hamburger-btn { display: block !important; }
        }
        @media (min-width: 769px) {
          .mobile-nav { display: none !important; }
        }
      `}</style>
    </nav>
  );
}
